import { useRef, useEffect, useState } from "react";
import CommonSelect from "../common/commonSelect";

interface FilterState {
  companyType: string | null;
  companyId: number | null;
  department: string | null;
  designation: string | null;
}

interface UploadModalProps {
  modalId: string;
  onUpload: () => void;
  selectedFile: File | null;
  handleFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  uploading?: boolean;
  documentType: string;
  onDocumentTypeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onModalClose?: () => void;
  customTitle?: any;
  // New props for filters
  filters?: FilterState;
  onFilterChange?: (filterType: keyof FilterState, value: any) => void;
  isBulkUpload?: boolean;
  selectedCount?: number;
  // Data for filters
  allEmployeesData?: any[];
  allCompanies?: any[];
  allDepartments?: any[];
  allDesignations?: any[];
  onFilteredEmployeesChange?: (filteredEmployees: any[]) => void;
}

const UploadModal: React.FC<UploadModalProps> = ({
  modalId,
  onUpload,
  selectedFile,
  handleFileUpload,
  uploading = false,
  documentType,
  onDocumentTypeChange,
  onModalClose,
  customTitle,
  // Filter props
  filters = {
    companyType: null,
    companyId: null,
    department: null,
    designation: null,
  },
  onFilterChange = () => { },
  isBulkUpload = false,
  selectedCount = 0,
  // Data props
  allEmployeesData = [],
  allCompanies = [],
  allDepartments = [],
  allDesignations = [],
  onFilteredEmployeesChange = () => { },
}) => {
  const allEmployees = allEmployeesData.filter(
    (emp: any) => emp.Status === "Active"
  );

  const dropRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [filtersApplied, setFiltersApplied] = useState(false);

  // Reset state when modal closes
  useEffect(() => {
    const modalElement = document.getElementById(modalId);
    if (modalElement) {
      modalElement.addEventListener("hidden.bs.modal", () => {
        if (onModalClose) {
          onModalClose();
        }
        if (fileInputRef.current) {
          fileInputRef.current.value = ""; // Reset file input
        }
        // Reset filtersApplied when modal closes
        setFiltersApplied(false);
      });
    }
  }, [modalId, onModalClose]);

  // Get company options for CommonSelect
  const getCompanyOptions = () => {
    if (!allCompanies || allCompanies.length === 0) return [];

    return [
      ...allCompanies.map((company: any) => ({
        value: company.id + "-" + company.type,
        label: company.name + " - " + company.type,
      })),
    ];
  };

  // Get department options based on company filter
  const getDepartmentOptions = () => {
    if (!filters.companyId || !allEmployees || allEmployees.length === 0) {
      return [
        { value: "all", label: "All Departments" },
        ...Array.from(new Set(allEmployees.map((emp: any) => emp.Department)))
          .filter((dept) => dept)
          .sort()
          .map((dept) => ({ value: dept, label: dept })),
      ];
    }

    // Filter employees by selected company
    const filteredEmployees = allEmployees.filter(
      (emp: any) =>
        emp.CompanyId === filters.companyId &&
        emp.CompanyType === filters.companyType
    );

    const departments = Array.from(
      new Set(filteredEmployees.map((emp: any) => emp.Department))
    )
      .filter((dept) => dept)
      .sort();

    return [
      { value: "all", label: "All Departments" },
      ...departments.map((dept) => ({ value: dept, label: dept })),
    ];
  };

  // Get designation options based on company and department filters
  const getDesignationOptions = () => {
    if (
      (!filters.companyId && !filters.department) ||
      !allEmployees ||
      allEmployees.length === 0
    ) {
      return [
        { value: "all", label: "All Designations" },
        ...Array.from(new Set(allEmployees.map((emp: any) => emp.Designation)))
          .filter((desig) => desig)
          .sort()
          .map((desig) => ({ value: desig, label: desig })),
      ];
    }

    // Filter employees by selected filters
    let filteredEmployees = allEmployees;

    if (filters.companyId && filters.companyType) {
      filteredEmployees = filteredEmployees.filter(
        (emp: any) =>
          emp.CompanyId === filters.companyId &&
          emp.CompanyType === filters.companyType
      );
    }

    if (filters.department && filters.department !== "all") {
      filteredEmployees = filteredEmployees.filter(
        (emp: any) => emp.Department === filters.department
      );
    }

    const designations = Array.from(
      new Set(filteredEmployees.map((emp: any) => emp.Designation))
    )
      .filter((desig) => desig)
      .sort();

    return [
      { value: "all", label: "All Designations" },
      ...designations.map((desig) => ({ value: desig, label: desig })),
    ];
  };

  // Apply filters and log results
  const applyFiltersAndLog = () => {
    if (!allEmployees || allEmployees.length === 0) return;

    let filteredEmployees = allEmployees;

    // Step 1: Filter by company
    if (filters.companyId && filters.companyType) {
      filteredEmployees = filteredEmployees.filter(
        (emp: any) =>
          emp.CompanyId === filters.companyId &&
          emp.CompanyType === filters.companyType
      );
    }

    // Step 2: Filter by department
    if (filters.department && filters.department !== "all") {
      filteredEmployees = filteredEmployees.filter(
        (emp: any) => emp.Department === filters.department
      );
    }

    // Step 3: Filter by designation
    if (filters.designation && filters.designation !== "all") {
      filteredEmployees = filteredEmployees.filter(
        (emp: any) => emp.Designation === filters.designation
      );
    }

    onFilteredEmployeesChange(filteredEmployees);

    // Check if any filter is actually applied (not just default values)
    const hasActiveFilters =
      (filters.companyId && filters.companyType) ||
      (filters.department && filters.department !== "all") ||
      (filters.designation && filters.designation !== "all");

    setFiltersApplied(!!hasActiveFilters && filteredEmployees.length > 0);

    return filteredEmployees;
  };

  // Drag drop handlers
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();

    // For bulk upload, check if filters are applied
    if (isBulkUpload && !filtersApplied) {
      alert("Please apply filters first before uploading files.");
      return;
    }

    const file = e.dataTransfer.files[0];
    if (file) {
      const fakeEvent = { target: { files: [file] } } as any;
      handleFileUpload(fakeEvent);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleFileInputClick = (e: React.MouseEvent<HTMLLabelElement>) => {
    // For bulk upload, check if filters are applied
    if (isBulkUpload && !filtersApplied) {
      e.preventDefault();
      alert("Please apply filters first before selecting files.");
      return;
    }
  };

  const handleCompanyChange = (value: any) => {
    if (!value) {
      onFilterChange("companyId", null);
      onFilterChange("companyType", null);
      onFilterChange("department", null);
      onFilterChange("designation", null);
      setFiltersApplied(false);
      return;
    }

    const [companyId, companyType] = value.value.split("-");
    onFilterChange("companyId", parseInt(companyId));
    onFilterChange("companyType", companyType);
    onFilterChange("department", null);
    onFilterChange("designation", null);
    setFiltersApplied(false);
  };

  const handleDepartmentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    onFilterChange("department", value === "all" ? null : value);
    onFilterChange("designation", null);
    setFiltersApplied(false);
  };

  const handleDesignationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    onFilterChange("designation", value === "all" ? null : value);
    setFiltersApplied(false);
  };

  return (
    <div className="modal fade" id={modalId} tabIndex={-1} aria-hidden="true">
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {customTitle || "Upload File"}
              {isBulkUpload && selectedCount > 0 && (
                <span className="badge bg-primary ms-2">
                  {selectedCount} selected
                </span>
              )}
            </h5>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>

          <div className="modal-body">
            {/* Filters Section - Only show for bulk upload or if explicitly enabled */}
            {isBulkUpload && (
              <div className="card mb-4">
                <div className="card-header d-flex align-items-center">
                  <h6 className="mb-0">Filter Employees</h6>
                  {filtersApplied && (
                    <span className="badge bg-success ms-2">
                      Filters Applied
                    </span>
                  )}
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Company</label>
                      <CommonSelect
                        className="select"
                        options={getCompanyOptions()}
                        defaultValue={
                          filters.companyId
                            ? {
                              value: `${filters.companyId}-${filters.companyType}`,
                              label: `${allCompanies.find(
                                (c: any) =>
                                  c.id === filters.companyId &&
                                  c.type === filters.companyType
                              )?.name || ""} - ${filters.companyType}`,
                            }
                            : undefined
                        }
                        onChange={handleCompanyChange}
                        placeHolder="Select Company"
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">Department</label>
                      <select
                        className="form-control"
                        value={filters.department || "all"}
                        onChange={handleDepartmentChange}
                        disabled={!filters.companyId}
                      >
                        {getDepartmentOptions().map((option, index) => (
                          <option key={index} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">Designation</label>
                      <select
                        className="form-control"
                        value={filters.designation || "all"}
                        onChange={handleDesignationChange}
                        disabled={!filters.department}
                      >
                        {getDesignationOptions().map((option, index) => (
                          <option key={index} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-6 mb-3 d-flex align-items-end">
                      <button
                        type="button"
                        className="btn btn-outline-secondary w-100"
                        onClick={applyFiltersAndLog}
                      >
                        {filtersApplied ? "Re-apply Filters" : "Apply Filters"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Document Type Input */}
            <div className="mb-3">
              <label htmlFor="documentType" className="form-label">
                Document Type <span className="text-danger"> *</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="documentType"
                value={documentType}
                onChange={onDocumentTypeChange}
                placeholder="Enter document type"
                disabled={isBulkUpload && !filtersApplied}
              />
            </div>

            {/* Drag drop zone */}
            <div
              className={`file-drop mb-3 text-center border rounded p-4 ${isBulkUpload && !filtersApplied
                ? "border-secondary bg-light"
                : "border-primary"
                }`}
              ref={dropRef}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              style={{
                cursor:
                  isBulkUpload && !filtersApplied ? "not-allowed" : "pointer",
                opacity: isBulkUpload && !filtersApplied ? 0.6 : 1,
              }}
              title={
                isBulkUpload && !filtersApplied
                  ? "Apply filters first to enable upload"
                  : ""
              }
            >
              {isBulkUpload && !filtersApplied ? (
                <>
                  <span className="avatar avatar-sm bg-secondary text-white mb-2">
                    <i className="ti ti-lock fs-16" />
                  </span>
                  <h6 className="mb-2 text-muted">
                    Apply filters to enable upload
                  </h6>
                  <p className="fs-12 mb-0 text-muted">
                    Please apply filters above before uploading files
                  </p>
                </>
              ) : (
                <>
                  <span className="avatar avatar-sm bg-primary text-white mb-2">
                    <i className="ti ti-upload fs-16" />
                  </span>
                  <h6 className="mb-2">Drop files here</h6>
                  <p className="fs-12 mb-0">
                    Or click below to choose files from your computer
                  </p>
                </>
              )}

              <div className="d-flex flex-column align-items-center mt-3">
                {/* File Selection */}
                <label
                  className={`btn ${isBulkUpload && !filtersApplied
                    ? "btn-secondary"
                    : "btn-outline-primary"
                    }`}
                  onClick={handleFileInputClick}
                  style={{
                    cursor:
                      isBulkUpload && !filtersApplied
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  {isBulkUpload && !filtersApplied
                    ? "Upload Disabled"
                    : "Choose File"}
                  <input
                    type="file"
                    onChange={handleFileUpload}
                    style={{ display: "none" }}
                    ref={fileInputRef}
                    disabled={isBulkUpload && !filtersApplied}
                  />
                </label>

                {selectedFile && (
                  <div className="mt-2 text-success fw-medium">
                    Selected: {selectedFile.name}
                  </div>
                )}

                {/* Save Button */}
                <div className="d-grid gap-2 mt-4">
                  <button
                    type="button"
                    className={`btn ${isBulkUpload && !filtersApplied
                      ? "btn-secondary"
                      : "btn-primary"
                      }`}
                    onClick={onUpload}
                    disabled={
                      !selectedFile ||
                      uploading ||
                      !documentType.trim() ||
                      (isBulkUpload && !filtersApplied)
                    }
                    title={
                      isBulkUpload && !filtersApplied
                        ? "Apply filters first"
                        : !documentType.trim()
                          ? "Document type is required"
                          : !selectedFile
                            ? "Please select a file"
                            : ""
                    }
                  >
                    {uploading ? "Uploading..." : "Save File"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;